import { useCallback, useState } from 'react';
import React from 'react';
import { pdf } from '@react-pdf/renderer';

import { getAllMyApplies } from '../api/jobboard/apply.api';
import JobSeekingCertificatePDF from '../components/MyPage/MyApply/Applications/JobSeekingCertificatePDF';

const pad = (n) => String(n).padStart(2, '0');

/** KR-20260911-000123 — 발급일 + 사용자 식별자 기반 (서버 저장 없이 재현 가능) */
const buildDocumentNo = (user, issuedAt) => {
  const d = issuedAt;
  const datePart = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
  const userPart = String(user?.id ?? 0).padStart(6, '0');
  return `KR-${datePart}-${userPart}`;
};

const sanitizeFileName = (value) =>
  String(value ?? '')
    .replace(/[\\/:*?"<>|]/g, '')
    .trim();

/**
 * 구직활동 증명서 PDF 다운로드.
 * 전체 지원내역을 조회해 국문 증명서를 만들고 브라우저로 내려받는다.
 */
const useJobSeekingCertificateDownload = (user) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const download = useCallback(
    async (period = 'ALL') => {
      if (isDownloading) return { ok: false, reason: 'BUSY' };

      setIsDownloading(true);
      try {
        const applies = await getAllMyApplies(period);
        const list = Array.isArray(applies) ? applies : [];

        const issuedAtDate = new Date();
        const element = React.createElement(JobSeekingCertificatePDF, {
          user,
          applies: list,
          period,
          issuedAt: issuedAtDate.toISOString(),
          documentNo: buildDocumentNo(user, issuedAtDate),
        });

        const blob = await pdf(element).toBlob();

        const name =
          sanitizeFileName(
            [user?.firstName, user?.lastName].filter(Boolean).join(' '),
          ) || 'Koready';
        const datePart = `${issuedAtDate.getFullYear()}${pad(
          issuedAtDate.getMonth() + 1,
        )}${pad(issuedAtDate.getDate())}`;

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `구직활동증명서_${name}_${datePart}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        return { ok: true, count: list.length };
      } catch (error) {
        console.error('Job seeking certificate download failed:', error);
        return { ok: false, reason: 'ERROR' };
      } finally {
        setIsDownloading(false);
      }
    },
    [user, isDownloading],
  );

  return { download, isDownloading };
};

export default useJobSeekingCertificateDownload;
